/**
 * 渠道列表接口 - 通用 API，各页面均可调用
 * GET /api/channels 返回系统已对接的渠道列表
 */
import { apiGet } from '@/utils/request';
import { parseListResponse } from '@/utils/apiResponse';
import type { ContentOption } from '@/types/conditionBuilder';

/** 渠道项（/api/channels 返回的 list 元素） */
export interface ChannelItem {
  /** 渠道 id */
  id: number;
  /** 渠道 key，用于其他接口关联 */
  key: string;
  /** 渠道名称 */
  name: string;
  /** 描述 */
  description: string;
  /** 授权类型 */
  auth_type: string | null;
  /** 状态 */
  status: number;
  /** 排序 */
  sort: number;
  /** 账号数量 */
  account_count: number;
}

/** 渠道列表查询参数 */
export interface ChannelListParams {
  /** 1=只要有账号的渠道，0=只要没账号的；不传返回全部 */
  has_account?: 0 | 1;
}

/** 获取渠道列表，决定系统共有哪些对接的渠道 */
export function queryChannelsList(params?: ChannelListParams): Promise<ChannelItem[]> {
  return apiGet('/channels', params as Record<string, unknown> | undefined).then((d) =>
    parseListResponse<ChannelItem>(d),
  );
}

/** 渠道账号项 */
export interface ChannelAccount {
  id: number;
  name: string;
  channel_id: number;
  status: number;
  auth_status: number;
  external_id: string;
  [property: string]: any;
}

/** 获取渠道下的账号列表；传 robotId 时只返回绑定在该机器人下的账号 */
export function queryChannelAccounts(
  channelId: number,
  robotId?: number,
): Promise<ChannelAccount[]> {
  return apiGet(
    `/channels/${channelId}/accounts`,
    robotId ? { robot_id: robotId } : undefined,
  ).then((d) => parseListResponse<ChannelAccount>(d));
}

/**
 * 渠道/账号选项：不做结果缓存，每次调用都重新拉取。
 *
 * 这类数据随登录账号、以及渠道管理里的账号增删改实时变化，
 * 缓存结果会导致换账号后串号、或新增账号后选不到。
 * 这里只保留「进行中的请求」用于并发去重——条件构建器每个条件行都会各调一次 fetcher，
 * 同一瞬间挂载的多行共享同一个请求；请求结束即释放，下次打开弹窗仍是最新数据。
 */
let accountOptionsInflight: Promise<ContentOption[]> | null = null;
let sourceOptionsInflight: Promise<ContentOption[]> | null = null;

/** 获取所有渠道下账号的扁平化选项（供条件构建器"账号 ID"使用） */
export function fetchAccountOptions(): Promise<ContentOption[]> {
  if (accountOptionsInflight) return accountOptionsInflight;
  const task = (async () => {
    const channels = await queryChannelsList();
    const grouped = await Promise.all(
      channels.map(async (ch) => {
        const accounts = await queryChannelAccounts(ch.id);
        return accounts.map((acc) => ({
          value: acc.id,
          label: `${ch.name} / ${acc.name}`,
        }));
      }),
    );
    return grouped.flat();
  })();
  accountOptionsInflight = task.finally(() => {
    accountOptionsInflight = null;
  });
  return accountOptionsInflight;
}

/** 渠道→账号级联选项的构建，全量与按机器人过滤共用 */
async function buildSourceOptions(robotId?: number): Promise<ContentOption[]> {
  const channels = await queryChannelsList();
  return Promise.all(
    channels.map(async (ch) => {
      const accounts = await queryChannelAccounts(ch.id, robotId);
      return {
        value: ch.id,
        label: ch.name,
        children: accounts.map((acc) => ({ value: acc.id, label: acc.name })),
      } as ContentOption;
    }),
  );
}

/**
 * 获取来源渠道级联选项（渠道→账号）。
 * 签名受条件构建器的 OptionsFetcherFn 契约约束，不要在此加参数——
 * 按机器人过滤请用 fetchRobotSourceOptions。
 */
export function fetchSourceOptions(): Promise<ContentOption[]> {
  if (sourceOptionsInflight) return sourceOptionsInflight;
  const task = buildSourceOptions();
  sourceOptionsInflight = task.finally(() => {
    sourceOptionsInflight = null;
  });
  return sourceOptionsInflight;
}

/**
 * 只取绑定在指定机器人下的渠道账号（AI 测试用）。
 * 结果口径与全量不同，不参与 sourceOptionsInflight 去重，避免两者互相串结果。
 */
export function fetchRobotSourceOptions(robotId: number): Promise<ContentOption[]> {
  return buildSourceOptions(robotId);
}
